/* global BigInt */
// NAPLAN item utilities — the single source of truth for marking and describing
// a child's response to any item type in scripts/naplan-expand/ITEM_SCHEMA.md.
// Every screen that marks or displays an answer uses these, never `r === q.answer`.
//
// Responses: mcq: index · multi: sorted index array · order: array of item texts ·
// match: array of category indices parallel to items · hottext: sorted index array ·
// short: the typed string · inline: index array.

export const ITEM_TYPES = ['mcq', 'multi', 'order', 'match', 'hottext', 'short', 'inline'];

export function itemType(q) {
  return (q && q.type) || 'mcq';
}

/** True for single-choice items (the only kind legacy screens can show). */
export function isMcqItem(q) {
  return itemType(q) === 'mcq';
}

function isNum(v) {
  return typeof v === 'number' && Number.isInteger(v);
}

function sortedUnique(arr) {
  return [...new Set(arr)].sort((a, b) => a - b);
}

function sameSet(a, b) {
  if (!Array.isArray(a) || !Array.isArray(b)) return false;
  if (!a.every(isNum)) return false;
  const x = sortedUnique(a);
  const y = sortedUnique(b);
  return x.length === y.length && x.every((v, i) => v === y[i]);
}

function sameSeq(a, b) {
  return Array.isArray(a) && Array.isArray(b) && a.length === b.length && a.every((v, i) => v === b[i]);
}

// ── short-response normalisation ─────────────────────────────────────
// Ignores spaces, case and thousands separators. Curly quotes are
// straightened so a phone keyboard's ’ matches a typed '.

function stripThousands(s) {
  // 12,345,678 → 12345678 (only well-formed digit groups; "3,4" is left alone)
  return s.replace(/\d{1,3}(?:,\d{3})+(?!\d)/g, m => m.replace(/,/g, ''));
}

export function normaliseShort(s) {
  if (s === null || s === undefined) return '';
  let t = String(s).replace(/[‘’]/g, "'").replace(/[“”]/g, '"').replace(/−/g, '-');
  t = t.trim().toLowerCase();
  t = stripThousands(t);
  t = t.replace(/(\d)[   ](?=\d{3}(?!\d))/g, '$1'); // 1 000 → 1000
  t = t.replace(/\s+/g, '');
  return t;
}

// Exact rational parsing: { n: BigInt, d: BigInt } or null.
function parseRational(raw, unit) {
  if (raw === null || raw === undefined) return null;
  let s = String(raw).replace(/−/g, '-').trim().toLowerCase();
  s = stripThousands(s);
  s = s.replace(/(\d)[   ](?=\d{3}(?!\d))/g, '$1');
  if (unit) {
    const u = String(unit).trim().toLowerCase();
    if (u && s.endsWith(u)) s = s.slice(0, -u.length).trim();
  }
  s = s.replace(/^\$\s*/, '').replace(/^(-)\s*\$\s*/, '$1');
  // mixed number "1 1/2" (before spaces are removed)
  let m = s.match(/^(-?)(\d+)\s+(\d+)\s*\/\s*(\d+)$/);
  if (m) {
    const d = BigInt(m[4]);
    if (d === BigInt(0)) return null;
    const n = BigInt(m[2]) * d + BigInt(m[3]);
    return { n: m[1] ? -n : n, d };
  }
  s = s.replace(/\s+/g, '');
  m = s.match(/^([-+]?)(\d+)\/(\d+)$/);
  if (m) {
    const d = BigInt(m[3]);
    if (d === BigInt(0)) return null;
    const n = BigInt(m[2]);
    return { n: m[1] === '-' ? -n : n, d };
  }
  m = s.match(/^([-+]?)(\d*)(?:\.(\d+))?$/);
  if (m && (m[2] || m[3])) {
    const frac = m[3] || '';
    const d = BigInt('1' + '0'.repeat(frac.length));
    const n = BigInt((m[2] || '0') + frac);
    return { n: m[1] === '-' ? -n : n, d };
  }
  return null;
}

function rationalEq(a, b) {
  return !!a && !!b && a.n * b.d === b.n * a.d;
}

/** Does a typed string match one expected form (numeric value or normalised text)? */
export function shortMatches(q, typed, expected) {
  if (q && q.numeric) {
    const a = parseRational(typed, q.unit);
    const b = parseRational(expected, q.unit);
    if (a && b) return rationalEq(a, b);
  }
  const t = normaliseShort(typed);
  if (!t) return false;
  if (t === normaliseShort(expected)) return true;
  // allow the child to type the unit that is already shown after the box
  if (q && q.unit) {
    const u = normaliseShort(q.unit);
    if (u && t.endsWith(u) && t.slice(0, -u.length) === normaliseShort(expected)) return true;
  }
  return false;
}

// ── marking ──────────────────────────────────────────────────────────

export function isCorrectResponse(q, r) {
  if (!q || r === null || r === undefined) return false;
  switch (itemType(q)) {
    case 'mcq':
      return isNum(r) && r === q.answer;
    case 'multi':
      return Array.isArray(r) && r.length === new Set(r).size && sameSet(r, q.answers || []);
    case 'order':
      return sameSeq(r, q.items || []);
    case 'match': {
      const items = q.items || [];
      return Array.isArray(r) && r.length === items.length && items.length > 0 && items.every((it, i) => r[i] === it.cat);
    }
    case 'hottext':
      return Array.isArray(r) && r.length === new Set(r).size && sameSet(r, q.answers || []);
    case 'short': {
      if (typeof r !== 'string' && typeof r !== 'number') return false;
      return (q.accept || []).some(a => shortMatches(q, String(r), a));
    }
    case 'inline': {
      const blanks = q.blanks || [];
      return Array.isArray(r) && r.length === blanks.length && blanks.length > 0 && blanks.every((b, i) => r[i] === b.answer);
    }
    default:
      return false;
  }
}

// ── display ──────────────────────────────────────────────────────────

const clean = s => (s === null || s === undefined ? '' : String(s).trim());

function fillTemplate(template, picks) {
  return String(template || '').replace(/\{(\d+)\}/g, (_, g) => {
    const v = picks[Number(g)];
    return v === undefined || v === null || v === '' ? '___' : v;
  });
}

function groupByCategory(q, cats) {
  const categories = q.categories || [];
  const items = q.items || [];
  return categories
    .map((c, ci) => {
      const inCat = items.filter((it, i) => cats[i] === ci).map(it => it.text);
      return inCat.length ? `${c}: ${inCat.join(', ')}` : null;
    })
    .filter(Boolean)
    .join('; ');
}

/** The child's response as readable text ("" when there is none). */
export function responseText(q, r) {
  if (!q || r === null || r === undefined) return '';
  const opts = q.options || [];
  switch (itemType(q)) {
    case 'mcq':
      return isNum(r) && opts[r] !== undefined ? clean(opts[r]) : '';
    case 'multi':
      return Array.isArray(r) ? sortedUnique(r).map(i => clean(opts[i])).filter(Boolean).join('; ') : '';
    case 'order':
      return Array.isArray(r) ? r.map(clean).join(' → ') : '';
    case 'match':
      return Array.isArray(r) ? groupByCategory(q, r) : '';
    case 'hottext':
      return Array.isArray(r) ? sortedUnique(r).map(i => clean((q.segments || [])[i])).filter(Boolean).join(' … ') : '';
    case 'short': {
      const t = clean(r);
      return t && q.unit ? `${t} ${q.unit}` : t;
    }
    case 'inline':
      return Array.isArray(r)
        ? fillTemplate(q.template, (q.blanks || []).map((b, i) => (isNum(r[i]) ? (b.options || [])[r[i]] : undefined)))
        : '';
    default:
      return '';
  }
}

/** The correct answer as readable text. */
export function answerText(q) {
  if (!q) return '';
  switch (itemType(q)) {
    case 'mcq':
      return clean((q.options || [])[q.answer]);
    case 'multi':
      return responseText(q, q.answers || []);
    case 'order':
      return (q.items || []).map(clean).join(' → ');
    case 'match':
      return groupByCategory(q, (q.items || []).map(it => it.cat));
    case 'hottext':
      return responseText(q, q.answers || []);
    case 'short': {
      const a = clean((q.accept || [])[0]);
      return a && q.unit ? `${a} ${q.unit}` : a;
    }
    case 'inline':
      return fillTemplate(q.template, (q.blanks || []).map(b => (b.options || [])[b.answer]));
    default:
      return '';
  }
}

// ── stored feedback ──────────────────────────────────────────────────

function joinMsgs(msgs) {
  const out = [];
  msgs.forEach(m => {
    const t = clean(m);
    if (t && !out.includes(t)) out.push(t);
  });
  return out.length ? out.join(' ') : null;
}

/**
 * The stored message for this specific wrong response, or null when the
 * response is correct / there is nothing stored for it.
 */
export function feedbackFor(q, r) {
  if (!q || r === null || r === undefined) return null;
  if (isCorrectResponse(q, r)) return null;
  const why = q.why;
  const dflt = clean(q.whyDefault) || null;
  switch (itemType(q)) {
    case 'mcq':
      return (Array.isArray(why) && isNum(r) && clean(why[r])) || dflt;
    case 'multi': {
      const chosen = Array.isArray(r) ? sortedUnique(r) : [];
      const answers = q.answers || [];
      const wrongPicks = chosen.filter(i => !answers.includes(i));
      const missed = answers.filter(i => !chosen.includes(i));
      if (!Array.isArray(why)) return dflt;
      const wrongMsg = joinMsgs(wrongPicks.map(i => why[i]));
      if (wrongMsg) return wrongMsg;
      const missedMsg = joinMsgs(missed.map(i => why[i]));
      return missedMsg ? `You missed one. ${missedMsg}` : dflt;
    }
    case 'order': {
      const w = why && typeof why === 'object' ? why : {};
      const items = q.items || [];
      if (Array.isArray(r) && items.length > 1 && sameSeq(r, [...items].reverse()) && clean(w.reversed)) return clean(w.reversed);
      return clean(w.default) || dflt;
    }
    case 'match': {
      const w = why && typeof why === 'object' ? why : {};
      const items = q.items || [];
      const misplaced = items.filter((it, i) => !Array.isArray(r) || r[i] !== it.cat);
      return joinMsgs(misplaced.map(it => w[it.text])) || dflt;
    }
    case 'hottext': {
      const w = why && typeof why === 'object' ? why : {};
      const answers = q.answers || [];
      const wrong = Array.isArray(r) ? sortedUnique(r).filter(i => !answers.includes(i)) : [];
      return joinMsgs(wrong.map(i => w[String(i)])) || dflt;
    }
    case 'short': {
      const w = why && typeof why === 'object' ? why : {};
      const key = Object.keys(w).find(k => shortMatches(q, String(r), k));
      return (key && clean(w[key])) || dflt;
    }
    case 'inline': {
      const w = why && typeof why === 'object' ? why : {};
      const blanks = q.blanks || [];
      const msgs = blanks.map((b, g) => (Array.isArray(r) && r[g] !== b.answer ? w[`${g}:${r[g]}`] : null));
      return joinMsgs(msgs) || dflt;
    }
    default:
      return dflt;
  }
}

// The pieces of text that ARE the correct answer (an option, an accepted short answer, a gap's word, a segment).
function correctTexts(q) {
  switch (itemType(q)) {
    case 'mcq': return [(q.options || [])[q.answer]];
    case 'multi': return (q.answers || []).map(i => (q.options || [])[i]);
    case 'short': return q.accept || [];
    case 'inline': return (q.blanks || []).map(b => (b.options || [])[b.answer]);
    case 'hottext': return (q.answers || []).map(i => (q.segments || [])[i]);
    default: return [];
  }
}

const normWords = (t) => String(t == null ? '' : t).toLowerCase().replace(/[^\p{L}\p{N}]+/gu, ' ').trim();
const escapeRe = (t) => t.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

/**
 * The stored feedback for a wrong response with the answer taken out, for a "try again" moment. Stored notes were
 * written to be read AFTER the answer is shown: numeracy notes end "Here is how: … = <answer>" and many others quote
 * the correct option. This keeps the diagnosis (what went wrong) and drops the worked answer and any sentence that
 * contains the correct answer's text. Returns null when nothing answer-free is left.
 */
export function retryHint(q, r) {
  const msg = feedbackFor(q, r);
  if (!msg) return null;
  const head = msg.split(/\s*Here is how:/)[0];
  const raw = correctTexts(q).map(t => String(t == null ? '' : t));
  const answers = raw.map(normWords).filter(Boolean);
  // the same answer in another form: its digits ("2 m 45 cm" -> 245 cm) or a spelling broken up ("suc-ces-sion")
  const digitForms = raw.map(t => t.replace(/\D/g, '')).filter(d => d.length >= 2);
  const wordForms = raw.map(t => t.trim().toLowerCase()).filter(t => /^\p{L}{5,}$/u.test(t));
  const leaks = (sentence) => {
    const n = normWords(sentence);
    if (answers.some(a => new RegExp(`(^|\\s)${escapeRe(a)}(\\s|$)`).test(n))) return true;
    const numbers = (sentence.match(/\d[\d,.]*/g) || []).map(x => x.replace(/\D/g, ''));
    if (digitForms.some(d => numbers.includes(d))) return true;
    const squashed = sentence.toLowerCase().replace(/[^\p{L}]+/gu, '');
    return wordForms.some(w => squashed.includes(w));
  };
  // sentence split without a lookbehind (older iOS Safari cannot parse one, and it would break the whole bundle)
  // a sentence ends at . ! or ? followed by a space, so "2.5" stays whole
  const parts = head.split(/([.!?]+\s+)/);
  const sentences = [];
  for (let i = 0; i < parts.length; i += 2) sentences.push((parts[i] + (parts[i + 1] || '')).trim());
  const kept = sentences.filter(t => t && !leaks(t));
  return kept.length ? kept.join(' ').trim() : null;
}

/** Per-part correctness, used by renderers to mark each part after submission. */
export function partMarks(q, r) {
  switch (itemType(q)) {
    case 'order':
      return (q.items || []).map((it, i) => Array.isArray(r) && r[i] === it);
    case 'match':
      return (q.items || []).map((it, i) => Array.isArray(r) && r[i] === it.cat);
    case 'inline':
      return (q.blanks || []).map((b, i) => Array.isArray(r) && r[i] === b.answer);
    default:
      return [];
  }
}

// Deterministic shuffle (seeded by the item id) so a remount or a
// "previous question" shows the same display order. Never returns the
// correct order when more than one item exists.
export function seededShuffle(arr, seed) {
  let h = 2166136261;
  const str = String(seed || '');
  for (let i = 0; i < str.length; i++) { h ^= str.charCodeAt(i); h = Math.imul(h, 16777619); }
  const rand = () => { h = Math.imul(h ^ (h >>> 15), 2246822507); h = Math.imul(h ^ (h >>> 13), 3266489909); h ^= h >>> 16; return (h >>> 0) / 4294967296; };
  const out = [...arr];
  for (let i = out.length - 1; i > 0; i--) {
    const j = Math.floor(rand() * (i + 1));
    [out[i], out[j]] = [out[j], out[i]];
  }
  if (out.length > 1 && out.every((v, i) => v === arr[i])) out.push(out.shift());
  return out;
}

/** The studentAnswer object every renderer passes to onSubmit. */
export function buildStudentAnswer(q, r) {
  return {
    questionId:   q && q.id,
    questionType: itemType(q),
    rawAnswer:    r,
    isCorrect:    isCorrectResponse(q, r),
    submittedAt:  new Date().toISOString(),
  };
}
